/**
 * Data tab for a `ZONE_SLIDER` widget — its DATA tier.
 *
 * Maps the control onto dataset columns and writes the mapping to
 * `widget.binding` (see `zone-slider-binding.utils`):
 *
 * - value / minimum / maximum / cost: one dataset, a column and a summary each.
 *   **Generate / Update Configuration** registers each mapped measure with
 *   `/components/compute` (like a bound text box) and the slider shows the
 *   API's numbers — the same flow, button and "Still needed" list as the
 *   Layout Builder's build form;
 * - zones: the inspector's static list, or one band per row of a dataset
 *   (label / from / to / colour columns — the colour column is optional).
 *   Zone rows are a lookup table, so they load as soon as they are mapped.
 *
 * The preview below the form shows exactly what the control will resolve to.
 */

import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgSelectModule } from '@ng-select/ng-select';
import { SsSelectTooltipDirective } from '../../../shared/ss-select-tooltip.directive';

import type { DesignNode } from '../../design-builder.types';
import { DesignDataService } from '../../services/design-data.service';
import { DesignRuntimeService } from '../../data/design-runtime.service';
import { tileDataset } from '../../data/design-tiles.utils';
import {
  EMPTY_ZONE_SLIDER_BINDING,
  applyZoneSliderBinding,
  compactZoneSliderBinding,
  zoneSliderBindingOf,
  zoneSliderDatasets,
  zoneSliderIsBound,
  zoneSliderMeasureBindings,
  zoneSliderStaticData,
  zoneSliderZonesDataset,
  type ZoneSliderBinding,
  type ZoneSliderLevel,
} from '../../renderers/zone-slider-binding.utils';
import { ZONE_SLIDER_PALETTE } from '../../../../global-components/zone-slider/zone-slider.utils';
import { resolveZoneSliderModel, type ZoneSliderModel } from '../../../../global-components/zone-slider/zone-slider.utils';

/** Column pickers of the value dataset: binding key → its summary key. */
const VALUE_FIELDS: ReadonlyArray<{ key: keyof ZoneSliderBinding; aggregation: keyof ZoneSliderBinding; label: string; hint: string }> = [
  { key: 'valueField', aggregation: 'valueAggregation', label: 'Handle (dragger)', hint: 'Where the handle sits' },
  { key: 'minField', aggregation: 'minAggregation', label: 'Minimum', hint: 'Left end of the track' },
  { key: 'maxField', aggregation: 'maxAggregation', label: 'Maximum', hint: 'Right end of the track' },
  { key: 'costField', aggregation: 'costAggregation', label: 'Cost basis', hint: 'For the {margin} token' },
  { key: 'marginField', aggregation: 'marginAggregation', label: 'Margin', hint: 'Margin % column; overrides cost' },
];

@Component({
  selector: 'app-design-zone-slider-data',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, MatIconModule, MatTooltipModule, NgSelectModule, SsSelectTooltipDirective],
  templateUrl: './zone-slider-data.component.html',
  styleUrl: './zone-slider-data.component.scss',
})
export class ZoneSliderDataComponent {
  protected readonly data = inject(DesignDataService);
  private readonly runtime = inject(DesignRuntimeService);

  readonly node = input.required<DesignNode>();
  /** The full binding to store on the widget (compact: only set keys). */
  readonly bindingChange = output<Record<string, unknown>>();
  /** Generate / Update Configuration: register the mapped measures with the compute API. */
  readonly generate = output<{ nodeId: string; binding: Record<string, unknown> }>();

  readonly valueFields = VALUE_FIELDS;
  /** The record id the slider got for the current selection (preview). */
  readonly recordIdNow = computed(() => {
    const state = this.computeState();
    return state?.status === 'ready' ? state.recordId ?? '' : '';
  });

  /** Record ID choices: the dataset's columns plus `uuid` (hidden from the column lists). Same array until it changes. */
  readonly recordIdOptions = computed(() => {
    const columns = this.columnsOf(this.binding().dataset);
    return columns.includes('uuid') ? columns : ['uuid', ...columns];
  });

  /** Minimum / Maximum / Cost basis / other zone sources. */
  readonly advancedOpen = signal(false);
  /** The dataset was filled in from the dashboard's other widgets. */
  readonly autoDataset = signal(false);

  /** The dataset most of the dashboard's other widgets read (for an empty slider). */
  private dashboardDataset(): string {
    const counts = new Map<string, number>();
    this.runtime.tiles().forEach((tile) => {
      if (tile.id === this.node().id) return;
      const dataset = tileDataset(tile);
      if (dataset) counts.set(dataset, (counts.get(dataset) ?? 0) + 1);
    });
    return [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? '';
  }

  readonly binding = computed(() => zoneSliderBindingOf(this.node().widget?.binding));
  readonly isBound = computed(() => zoneSliderIsBound(this.binding()));
  readonly zonesDataset = computed(() => zoneSliderZonesDataset(this.binding()));

  readonly datasetOptions = computed(() => {
    const state = this.data.datasets();
    return state.status === 'ready' ? state.value : [];
  });

  // ── Generate ───────────────────────────────────────────────────────────────

  /** Generated once = it carries a componentId (the compute registration key). */
  readonly isGenerated = computed(() => !!this.node().widget?.componentId);

  readonly computeState = computed(() => this.runtime.zoneValues()[this.node().id] ?? null);
  readonly isGenerating = computed(() => this.computeState()?.status === 'loading');

  readonly generateBlockers = computed<string[]>(() => {
    const binding = this.binding();
    if (!binding.dataset) return ['Dataset'];
    if (binding.zonesMode === 'columns' && binding.levels.some((level) => !level.field && !level.fromField)) {
      return ['A From or To column for every block'];
    }
    return zoneSliderMeasureBindings(binding).length ? [] : ['A column for the handle, Minimum, Maximum, Cost basis or a level'];
  });

  readonly canGenerate = computed(() => !this.isGenerating() && !this.generateBlockers().length);

  readonly generateLabel = computed(() => {
    if (this.isGenerating()) return this.isGenerated() ? 'Updating...' : 'Generating...';
    return this.isGenerated() ? 'Update Configuration' : 'Generate Configuration';
  });

  readonly generateBlockerText = computed(() =>
    this.generateBlockers().length ? `Still needed: ${this.generateBlockers().join(', ')}` : '');

  generateConfiguration(): void {
    if (!this.canGenerate()) return;
    this.generate.emit({ nodeId: this.node().id, binding: compactZoneSliderBinding(this.binding()) });
  }

  constructor() {
    this.data.loadDatasets();
    // No dataset yet: use the one the rest of the dashboard reads, so the column
    // lists are ready without asking.
    effect(() => {
      const node = this.node();
      untracked(() => {
        if (zoneSliderBindingOf(node.widget?.binding).dataset) return;
        const dataset = this.dashboardDataset();
        if (!dataset) return;
        this.autoDataset.set(true);
        this.setDataset(dataset);
      });
    });
    // Columns for the pickers, and the zone table's rows, whenever the mapping changes.
    effect(() => {
      const binding = this.binding();
      untracked(() => {
        [binding.dataset, this.zonesDataset()].filter(Boolean).forEach((dataset) => this.data.loadColumns(dataset));
        zoneSliderDatasets(binding).forEach((dataset) => this.data.loadRows(dataset));
      });
    });
  }

  // ── Columns ────────────────────────────────────────────────────────────────

  columnsOf(dataset: string): string[] {
    const state = dataset ? this.data.columns(dataset) : null;
    return state?.status === 'ready' ? state.value : [];
  }

  columnPlaceholder(dataset: string): string {
    if (!dataset) return 'Select a dataset first';
    const state = this.data.columns(dataset);
    if (state.status === 'loading') return 'Loading columns…';
    if (state.status === 'error') return 'Could not load columns';
    return 'Select a column';
  }

  columnsError(dataset: string): string {
    const state = dataset ? this.data.columns(dataset) : null;
    return state?.status === 'error' ? state.message : '';
  }

  // ── Writes ─────────────────────────────────────────────────────────────────

  setDataset(dataset: string | null): void {
    const next = dataset ?? '';
    const current = this.binding();
    if (next === current.dataset) return;
    // Columns belong to the old dataset; start the mapping over - the blocks'
    // columns too (they kept the old table's names, which the new one lacks).
    this.write({
      ...current,
      dataset: next,
      valueField: '', minField: '', maxField: '', costField: '', marginField: '', minCompareFields: [], maxCompareFields: [],
      levels: current.levels.map((level) => ({ ...level, field: '', fromField: '' })),
    });
  }

  setCompareFields(key: 'minCompareFields' | 'maxCompareFields', value: string[] | null): void {
    const current = this.binding();
    const next = { ...current, [key]: value ?? [] };
    // First pick: start at -10% (minimum) / +10% (maximum), as for Set Price - editable.
    if (key === 'minCompareFields' && next.minCompareFields.length && current.minAdjustPct === '') next.minAdjustPct = '-10';
    if (key === 'maxCompareFields' && next.maxCompareFields.length && current.maxAdjustPct === '') next.maxAdjustPct = '10';
    this.write(next);
  }

  /** A typed number in the Minimum / Maximum value list. */
  readonly addBoundNumber = (term: string) => term.trim();

  setAdjustPct(key: 'minAdjustPct' | 'maxAdjustPct', value: unknown): void {
    this.write({ ...this.binding(), [key]: value === null || value === undefined ? '' : String(value) });
  }

  readonly pickOptions = [
    { label: 'Lowest of them', value: 'lowest' },
    { label: 'Highest of them', value: 'highest' },
  ];

  setField(key: keyof ZoneSliderBinding, value: string | null): void {
    this.write({ ...this.binding(), [key]: value ?? '' });
  }

  /** Margin formula + the dataset columns it names (`[total_cost]`), so Generate fetches them. */
  setMarginFormula(formula: string | null): void {
    const text = formula ?? '';
    const keyOf = (name: string) => name.toLowerCase().replace(/[^a-z0-9]/g, '');
    const columns = this.columnsOf(this.binding().dataset);
    const named = Array.from(text.matchAll(/\[([^\]]+)\]/g), (match) => keyOf(match[1]));
    const marginColumns = Array.from(new Set(named
      .map((key) => columns.find((col) => keyOf(col) === key))
      .filter((col): col is string => !!col)));
    this.write({ ...this.binding(), marginFormula: text, marginColumns });
  }

  setZonesMode(mode: 'static' | 'rows' | 'columns'): void {
    const current = this.binding();
    // First switch to columns: start from the blocks drawn in the Design tab.
    const levels = mode === 'columns' && !current.levels.length ? this.levelsFromDesign() : current.levels;
    this.write({ ...current, zonesMode: mode, levels });
  }

  // ── Levels from columns ────────────────────────────────────────────────────

  addLevel(): void {
    const levels = this.binding().levels;
    this.write({ ...this.binding(), levels: [...levels, {
      label: `Block ${levels.length + 1}`,
      color: ZONE_SLIDER_PALETTE[levels.length % ZONE_SLIDER_PALETTE.length],
      // A new block starts where the last one ends.
      fromField: levels[levels.length - 1]?.field ?? '', fromAggregation: levels[levels.length - 1]?.aggregation ?? 'SUM',
      field: '', aggregation: 'SUM',
    }] });
  }

  setLevel(index: number, patch: Partial<ZoneSliderLevel>): void {
    const levels = this.binding().levels.map((level, i) => (i === index ? { ...level, ...patch } : level));
    this.write({ ...this.binding(), levels });
  }

  removeLevel(index: number): void {
    this.write({ ...this.binding(), levels: this.binding().levels.filter((_, i) => i !== index) });
  }

  moveLevel(index: number, delta: number): void {
    const levels = [...this.binding().levels];
    const to = index + delta;
    if (to < 0 || to >= levels.length) return;
    [levels[index], levels[to]] = [levels[to], levels[index]];
    this.write({ ...this.binding(), levels });
  }

  /** The Design tab's zones as starting levels (labels and colours, no columns yet). */
  private levelsFromDesign(): ZoneSliderLevel[] {
    const zones = (zoneSliderStaticData(this.node().widget!)['zones'] as Array<Record<string, unknown>> | undefined) ?? [];
    const source = zones.length ? zones : [{}, {}, {}, {}];
    return source.map((zone, index) => ({
      label: typeof zone['label'] === 'string' && zone['label'] ? zone['label'] : `Block ${index + 1}`,
      color: typeof zone['color'] === 'string' && zone['color'] ? zone['color'] : ZONE_SLIDER_PALETTE[index % ZONE_SLIDER_PALETTE.length],
      field: '', aggregation: 'SUM', fromField: '', fromAggregation: 'SUM',
    }));
  }

  setZonesDataset(dataset: string | null): void {
    const next = dataset ?? '';
    const current = this.binding();
    if (next === current.zonesDataset) return;
    this.write({ ...current, zonesDataset: next, zoneLabelField: '', zoneFromField: '', zoneToField: '', zoneColorField: '' });
  }

  unbind(): void {
    this.write({ ...EMPTY_ZONE_SLIDER_BINDING });
  }

  reload(): void {
    this.data.loadDatasets(true);
    this.data.refreshValues();
  }

  // ── Preview ────────────────────────────────────────────────────────────────

  /** What the control resolves to right now — static config with the API's values and zone rows on top. */
  readonly preview = computed<ZoneSliderModel | null>(() => {
    const widget = this.node().widget;
    if (!widget) return null;
    const compute = this.computeState();
    const resolved = applyZoneSliderBinding(
      zoneSliderStaticData(widget),
      this.binding(),
      (dataset) => {
        const state = this.data.rows(dataset);
        return state.status === 'ready' ? state.value : null;
      },
      compute?.status === 'ready' ? compute.values : null,
    );
    return resolveZoneSliderModel(resolved);
  });

  /**
   * Each block's computed From / To, and plain-language warnings about the
   * values that came back: a block with no width, one running backwards, two
   * covering the same stretch (only one colour shows), a column with no number,
   * and a Minimum / Maximum column overriding the blocks' own ends.
   */
  readonly blockValues = computed<Array<{ from: number | null; to: number | null }>>(() => {
    const compute = this.computeState();
    const values = compute?.status === 'ready' ? compute.values : {};
    return this.binding().levels.map((_, index) => ({
      from: typeof values[`levelFrom${index}`] === 'number' ? values[`levelFrom${index}`]! : null,
      to: typeof values[`level${index}`] === 'number' ? values[`level${index}`]! : null,
    }));
  });

  readonly blockWarnings = computed<string[]>(() => {
    const binding = this.binding();
    if (binding.zonesMode !== 'columns') return [];
    // A column the chosen dataset does not have - the backend rejects it (400).
    const columns = this.columnsOf(binding.dataset);
    if (columns.length) {
      const missing = binding.levels.flatMap((level) => [level.fromField, level.field])
        .filter((field) => field && !columns.includes(field));
      if (missing.length) {
        return [`${Array.from(new Set(missing)).join(', ')} ${missing.length === 1 ? 'is' : 'are'} not in "${binding.dataset}". Pick the dataset that has ${missing.length === 1 ? 'this column' : 'these columns'} in "Data from", or choose the block columns again.`];
      }
    }
    const compute = this.computeState();
    if (compute?.status !== 'ready') return [];
    const warnings: string[] = [];
    const name = (index: number) => binding.levels[index]?.label || `Block ${index + 1}`;
    const values = this.blockValues();
    // Requests that never got an answer (server or database down) are not a
    // column problem - say so once instead of blaming every column.
    const unreachable = (compute.message ?? '').split(' · ').filter((part) => part.endsWith(': could not compute')).length;
    if (unreachable) {
      warnings.push(`${unreachable} value${unreachable === 1 ? '' : 's'} could not be loaded - the server did not answer. Check that the backend and its database are running, then press Update Configuration again.`);
    }
    const failed = (key: string) => (compute.message ?? '').includes(`${key}: could not compute`);
    binding.levels.forEach((level, index) => {
      const { from, to } = values[index];
      if (level.fromField && from === null && !failed(`levelFrom${index}`)) warnings.push(`${name(index)}: "${level.fromField}" has no number for the current selection - it may be empty for these rows, or not a numeric column.`);
      if (level.field && to === null && !failed(`level${index}`)) warnings.push(`${name(index)}: "${level.field}" has no number for the current selection - it may be empty for these rows, or not a numeric column.`);
      if (from !== null && to !== null && from === to) warnings.push(`${name(index)} starts and ends at the same value (${from}), so it has no width and is not drawn.`);
      if (from !== null && to !== null && from > to) warnings.push(`${name(index)} runs backwards (From ${from} is above To ${to}) - check its two columns.`);
    });
    // Overlaps: two blocks covering the same stretch hide each other.
    const spans = values.map((value, index) => ({ index, low: Math.min(value.from ?? Infinity, value.to ?? Infinity), high: Math.max(value.from ?? -Infinity, value.to ?? -Infinity) }))
      .filter((span) => Number.isFinite(span.low) && Number.isFinite(span.high) && span.high > span.low);
    spans.forEach((a, i) => spans.slice(i + 1).forEach((b) => {
      const overlap = Math.min(a.high, b.high) - Math.max(a.low, b.low);
      if (overlap > 0) warnings.push(`${name(a.index)} and ${name(b.index)} cover the same values (${Math.max(a.low, b.low)}–${Math.min(a.high, b.high)}), so one colour hides the other. Their columns are probably not in the same units (e.g. a margin % next to a price).`);
    }));
    if (binding.minField || binding.maxField) {
      warnings.push(`Advanced has a ${[binding.minField && 'Minimum', binding.maxField && 'Maximum'].filter(Boolean).join(' and ')} column, so the track ends come from there, not from the blocks. Clear it to fit the track to the blocks.`);
    }
    return warnings;
  });

  readonly previewStatus = computed<{ text: string; error: boolean }>(() => {
    const binding = this.binding();
    const measures = zoneSliderMeasureBindings(binding);
    const compute = this.computeState();
    const parts: string[] = [];
    let error = false;

    if (measures.length) {
      if (!compute) parts.push(this.isGenerated()
        ? 'Mapping changed — press Update Configuration to fetch the new values.'
        : 'Press Generate Configuration to fetch the values from the compute API.');
      else if (compute.status === 'loading') parts.push('Fetching values from the compute API…');
      else if (compute.status === 'error') { parts.push(compute.message); error = true; }
      else {
        const got = Object.keys(compute.values).length;
        parts.push(`${got} of ${measures.length} value${measures.length === 1 ? '' : 's'} from the compute API`);
        if (compute.message) { parts.push(compute.message); error = true; }
      }
    } else {
      parts.push('No value columns mapped — the thumb, range and cost stay as set in the Design tab.');
    }

    zoneSliderDatasets(binding).forEach((dataset) => {
      const state = this.data.rows(dataset);
      if (state.status === 'error') { parts.push(state.message); error = true; }
      else if (state.status !== 'ready') parts.push(`Loading zone rows from "${dataset}"…`);
      else parts.push(`${state.value.length} zone rows in ${dataset}`);
    });

    return { text: parts.join(' · '), error };
  });

  trackByIndex(index: number): number {
    return index;
  }

  private write(binding: ZoneSliderBinding): void {
    this.bindingChange.emit(compactZoneSliderBinding(binding));
  }
}
